import {
  ArrowLeft,
  BookOpen,
  Calendar,
  Contact,
  CreditCard,
  Database,
  HardDrive,
  Linkedin,
  Mail,
  MessageSquare,
} from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { useConnectors } from '@/lib/hooks/use-connectors'
import type { ConnectorType } from '@/lib/types'
import { ConnectorTypeDetail } from './ConnectorTypeDetail'

interface ConnectorTypeConfig {
  type: ConnectorType
  name: string
  description: string
  icon: React.ReactNode
  available: boolean
}

const CONNECTOR_TYPES: ConnectorTypeConfig[] = [
  {
    type: 'postgres',
    name: 'Database',
    description: 'PostgreSQL, MySQL, and more',
    icon: <Database className="size-8" />,
    available: true,
  },
  {
    type: 'resend',
    name: 'Email',
    description: 'Send emails via Resend',
    icon: <Mail className="size-8" />,
    available: true,
  },
  {
    type: 'slack',
    name: 'Slack',
    description: 'Send messages to Slack channels',
    icon: <MessageSquare className="size-8" />,
    available: true,
  },
  {
    type: 'stripe',
    name: 'Stripe',
    description: 'Revenue, customers, invoices',
    icon: <CreditCard className="size-8" />,
    available: true,
  },
  {
    type: 'gmail',
    name: 'Gmail',
    description: 'Read and send emails via Gmail',
    icon: <Mail className="size-8" />,
    available: true,
  },
  {
    type: 'linkedin',
    name: 'LinkedIn',
    description: 'Post to LinkedIn',
    icon: <Linkedin className="size-8" />,
    available: true,
  },
  {
    type: 'google_calendar',
    name: 'Google Calendar',
    description: 'Read and manage calendar events',
    icon: <Calendar className="size-8" />,
    available: true,
  },
  {
    type: 'google_drive',
    name: 'Google Drive',
    description: 'Browse and manage files',
    icon: <HardDrive className="size-8" />,
    available: true,
  },
  {
    type: 'google_docs',
    name: 'Google Docs',
    description: 'Read and create documents',
    icon: <BookOpen className="size-8" />,
    available: true,
  },
  {
    type: 'google_contacts',
    name: 'Google Contacts',
    description: 'Search and manage contacts',
    icon: <Contact className="size-8" />,
    available: true,
  },
]

interface ConnectorsPageProps {
  onClose: () => void
}

export function ConnectorsPage({ onClose }: ConnectorsPageProps) {
  const [selectedType, setSelectedType] = useState<ConnectorType | null>(null)
  const { connectors } = useConnectors()

  // Count connections per type
  const connectionCounts = connectors.reduce(
    (acc, c) => {
      acc[c.type] = (acc[c.type] || 0) + 1
      return acc
    },
    {} as Record<string, number>
  )

  if (selectedType) {
    return (
      <ConnectorTypeDetail
        type={selectedType}
        onBack={() => setSelectedType(null)}
      />
    )
  }

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex items-center gap-2 p-3 border-b">
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="size-8"
        >
          <ArrowLeft className="size-4" />
        </Button>
        <h1 className="text-sm font-semibold">Connectors</h1>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <p className="text-xs text-muted-foreground mb-4">
          Connect external services to use with the AI assistant.
        </p>

        {/* Connector Types Grid */}
        <div className="grid grid-cols-2 gap-3">
          {CONNECTOR_TYPES.map((connectorType) => {
            const count = connectionCounts[connectorType.type] || 0
            const isAvailable = connectorType.available

            return (
              <button
                key={connectorType.type}
                onClick={() =>
                  isAvailable && setSelectedType(connectorType.type)
                }
                disabled={!isAvailable}
                className={`
                  relative flex flex-col items-center gap-2 p-4 rounded-lg border text-center
                  transition-all
                  ${
                    isAvailable
                      ? 'hover:border-primary hover:bg-muted/50 cursor-pointer'
                      : 'opacity-50 cursor-not-allowed'
                  }
                `}
              >
                <div
                  className={`
                    p-3 rounded-lg
                    ${isAvailable ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}
                  `}
                >
                  {connectorType.icon}
                </div>
                <div>
                  <p className="text-sm font-medium">{connectorType.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {connectorType.description}
                  </p>
                </div>
                {count > 0 && (
                  <span className="absolute top-2 right-2 text-xs bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full">
                    {count}
                  </span>
                )}
                {!isAvailable && (
                  <span className="absolute top-2 right-2 text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded">
                    Soon
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}
